import { intersectionBy, sortBy } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { getApplicationConfig } from '~/data/applicationConfig';
import { CompanyData, fetchCompanyData } from '~/data/company';
import { useCompanyData } from '~/data/company/useCompanyData';
import { useAllEmployees } from '~/data/employees';
import { Employees } from '~/data/employees/types';
import { useAllOrgUnits } from '~/data/orgunits';
import {
  getMyAltDirUnits,
  getMyDeputyDirUnits,
  getTeamEmployeeList,
  sortSearchItems,
} from '~/data/search';
import { Roles } from '~/data/user';
import {
  hasOneOfRoles,
  isRoleAltManager,
  isRoleDepManager,
  isRoleManager,
} from '~/services/Permissions';
import { AppDispatch } from '~/store';

export type GroupedOrgUnitEmps = {
  name: string;
  number: string;
  employees: Employees[];
};

export type UseEmployeesSearchOutput = {
  allEmployees: Employees[];
  getSearchResults: (searchQuery: string) => Employees[];
  getTeamMemberList: () => Employees[];
  getMatrixTeamMemberList: () => Employees[];
  getNewEmployeeList: () => Employees[];
  getEditableEndedEmpList: () => Employees[];
  getGroupedOrgUnitEmployees: () => GroupedOrgUnitEmps[];
  showEditableEndedEmployeeTab: boolean;
  hasMatrixTeam: boolean;
};

const getFilteredEmployees = (
  allEmployees: Employees[] | undefined,
  myUnits: number[],
  myId: number,
) =>
  allEmployees?.filter(
    (emp) => myUnits.indexOf(emp.fEmpUnit) >= 0 && emp.fEmpId !== myId,
  ) || [];

const getMatrixTeamEmployees = (
  loggedInEmpId: number,
  loggedInEmpRoles: Roles[] | string[] = [],
  allEmployees: Employees[] = [],
  secondaryTreeMatrixEmpList: Employees[] = [],
  loggedInDepDirUnits: number[] = [],
) => {
  if (isRoleDepManager(loggedInEmpRoles) && loggedInDepDirUnits.length) {
    return [
      ...secondaryTreeMatrixEmpList,
      ...getFilteredEmployees(allEmployees, loggedInDepDirUnits, loggedInEmpId),
    ];
  }

  return secondaryTreeMatrixEmpList;
};

const findTeamEmployees = (
  loggedInEmpId: number,
  loggedInEmpRoles: Roles[] | string[] = [],
  allEmployees: Employees[] = [],
  teamEmployeeList: Employees[] = [],
  loggedInDepDirUnits: number[] = [],
  loggedInAltDirUnits: number[] = [],
): Employees[] => {
  if (isRoleDepManager(loggedInEmpRoles) && loggedInDepDirUnits.length) {
    return [
      ...teamEmployeeList,
      ...getFilteredEmployees(allEmployees, loggedInDepDirUnits, loggedInEmpId),
    ];
  }

  if (isRoleAltManager(loggedInEmpRoles) && loggedInAltDirUnits.length) {
    return [
      ...teamEmployeeList,
      ...getFilteredEmployees(allEmployees, loggedInAltDirUnits, loggedInEmpId),
    ];
  }

  return teamEmployeeList;
};

const fetchAllSearchData = async (
  loggedInEmpId: number,
  loggedInEmpRoles: Roles[] | string[],
) =>
  Promise.all([
    isRoleManager(loggedInEmpRoles)
      ? getTeamEmployeeList(1, loggedInEmpId)
      : Promise.resolve([]),
    isRoleDepManager(loggedInEmpRoles)
      ? getMyDeputyDirUnits(loggedInEmpId)
      : Promise.resolve([]),
    isRoleAltManager(loggedInEmpRoles)
      ? getMyAltDirUnits(loggedInEmpId)
      : Promise.resolve([]),
    getApplicationConfig(),
  ]).then(
    ([
      teamEmployeeList,
      myDeputyDirUnits,
      myAltDirUnits,
      applicationConfig,
    ]) => {
      if (!isRoleManager(loggedInEmpRoles)) {
        return Promise.resolve({
          teamEmployeeList,
          myDeputyDirUnits,
          myAltDirUnits,
          matrixTeamEmployeeList: [],
          hasMatrixTeam: false,
        });
      }

      const secondaryTreeId =
        applicationConfig?.fComConfig.coreConfig.fComSecondaryTreeId;

      return getTeamEmployeeList(secondaryTreeId || 1, loggedInEmpId).then(
        (matrixTeamEmployeeList) => ({
          teamEmployeeList,
          myDeputyDirUnits,
          myAltDirUnits,
          matrixTeamEmployeeList,
          hasMatrixTeam: !!(
            secondaryTreeId &&
            matrixTeamEmployeeList.find(
              (emp) => emp.fEmpManagerId === loggedInEmpId,
            )
          ),
        }),
      );
    },
  );

const getSearchableItems = (
  allEmployees: Employees[],
  newEmployees: Employees[],
  editableEndedEmployees: Employees[],
  companyData?: CompanyData,
) => {
  const searchEmps = allEmployees.concat(newEmployees);
  var editingLimit =
    companyData?.config?.fComFormerEmployeeDataEditingLimit || 0;

  // All Ended employees
  if (editingLimit === -1) {
    return searchEmps.concat(editableEndedEmployees);
  }

  // Ended employees in within limit
  if (editingLimit > 0 && editingLimit <= 180) {
    const endedEmployeesWithinLimit = editableEndedEmployees.filter((emp) => {
      return moment().diff(emp.fEmplEndDate, 'days') <= editingLimit;
    });

    return searchEmps.concat(endedEmployeesWithinLimit);
  }

  // No Ended employees
  return searchEmps;
};

const normalizeString = (str: string): string =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const findMatchingItems = (
  searchQuery: string,
  searchableItems: Employees[],
) => {
  const normalizedQuery = normalizeString(searchQuery);

  return sortSearchItems(
    searchableItems?.filter((item) => {
      const fieldsToSearch = [
        item.fEmpFirstName,
        item.fEmpLastName,
        item.fEmpPosition,
        item.fEmpPersonNumber,
        `${item.fEmpLastName} ${item.fEmpFirstName}`,
        `${item.fEmpFirstName} ${item.fEmpLastName}`,
      ].filter(Boolean);

      return fieldsToSearch.some((field) =>
        normalizeString(field).includes(normalizedQuery),
      );
    }),
  );
};

export const useSearchData = (): UseEmployeesSearchOutput => {
  const dispatch: AppDispatch = useDispatch();
  const [rawEmpRelationData, setRawEmpRelationData] = useState<{
    teamEmployeeList: Employees[];
    myDeputyDirUnits: number[];
    myAltDirUnits: number[];
    matrixTeamEmployeeList: Employees[];
    hasMatrixTeam?: boolean;
  }>({
    teamEmployeeList: [],
    myDeputyDirUnits: [],
    myAltDirUnits: [],
    matrixTeamEmployeeList: [],
  });
  const myId = useLoggedInEmployeeId();
  const myRoles = useLoggedInUserRoles();
  const {
    canViewEndedEmployees,
    canViewNewEmployees,
    canViewEndedEmployeesTab,
    canSearchExtendedEmployeeSet,
  } = useMemo(() => {
    const extendedAccessHRRoles = [
      Roles.HR_ADMIN,
      Roles.HR_ADMIN_LIMITED,
      Roles.HR_STAFF,
      Roles.HR_DEV,
      Roles.HR_PEOP,
      Roles.HR_PEOP_DEV,
    ];

    const canViewNewEmployees = hasOneOfRoles(myRoles, [
      ...extendedAccessHRRoles,
      Roles.IT_ADMIN,
      Roles.MANAGER,
      Roles.ALT_MANAGER,
      Roles.RECRUITER,
      Roles.HR_SALARY,
    ]);

    const canViewEndedEmployees = hasOneOfRoles(myRoles, [
      ...extendedAccessHRRoles,
      Roles.IT_ADMIN,
      Roles.MANAGER,
      Roles.ALT_MANAGER,
      Roles.RECRUITER,
    ]);

    const canViewEndedEmployeesTab = hasOneOfRoles(myRoles, [
      ...extendedAccessHRRoles,
      Roles.IT_ADMIN,
      Roles.RECRUITER,
    ]);

    const canSearchExtendedEmployeeSet = hasOneOfRoles(myRoles, [
      ...extendedAccessHRRoles,
      Roles.IT_ADMIN,
      Roles.MANAGER,
      Roles.ALT_MANAGER,
    ]);

    return {
      canViewNewEmployees,
      canViewEndedEmployees,
      canViewEndedEmployeesTab,
      canSearchExtendedEmployeeSet,
    };
  }, [myRoles]);

  const { companyData } = useCompanyData();
  const { orgUnits } = useAllOrgUnits();

  const {
    activeEmployees: activeEmployeesObject,
    newEmployees: newEmployeesObject,
    editableEndedEmployees: editableEndedEmployeesObject,
    restrictedEndedEmployees: accessibleEndedEmployeesObject,
  } = useAllEmployees();

  const showEditableEndedEmployeeTab = useMemo(
    () =>
      !!companyData?.config?.fComFormerEmployeeDataEditingLimit &&
      canViewEndedEmployeesTab &&
      !!Object.keys(editableEndedEmployeesObject).length,
    [companyData, canViewEndedEmployeesTab, editableEndedEmployeesObject],
  );

  const allEmployees = useMemo(
    () => sortSearchItems(Object.values(activeEmployeesObject)),
    [activeEmployeesObject],
  );

  useEffect(() => {
    let isMounted = true;

    dispatch(fetchCompanyData());
    fetchAllSearchData(myId, myRoles).then((empRelationData) => {
      if (isMounted) {
        setRawEmpRelationData(empRelationData);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [dispatch, myId, myRoles, setRawEmpRelationData]);

  const getTeamMemberList = useCallback(() => {
    return sortSearchItems(
      findTeamEmployees(
        myId,
        myRoles,
        allEmployees,
        rawEmpRelationData.teamEmployeeList,
        rawEmpRelationData.myDeputyDirUnits,
        rawEmpRelationData.myAltDirUnits,
      ),
    );
  }, [myId, myRoles, allEmployees, rawEmpRelationData]);

  const getMatrixTeamMemberList = useCallback(() => {
    return sortSearchItems(
      getMatrixTeamEmployees(
        myId,
        myRoles,
        allEmployees,
        rawEmpRelationData.hasMatrixTeam
          ? rawEmpRelationData.matrixTeamEmployeeList
          : [],
        rawEmpRelationData.myDeputyDirUnits,
      ),
    );
  }, [myId, myRoles, allEmployees, rawEmpRelationData]);

  const getNewEmployeeList = useCallback((): Employees[] => {
    return canViewNewEmployees
      ? sortSearchItems(Object.values(newEmployeesObject))
      : [];
  }, [canViewNewEmployees, newEmployeesObject]);

  const getEditableEndedEmpList = useCallback((): Employees[] => {
    if (canViewEndedEmployees) {
      const editableEnded = Object.values(editableEndedEmployeesObject);

      if (canViewEndedEmployeesTab) {
        return sortSearchItems(editableEnded);
      }

      // NOTE: Quickfix restriction, only direct manager can find ended employee
      const accessibleEditableEnded = intersectionBy(
        editableEnded,
        Object.values(accessibleEndedEmployeesObject),
        'fEmpId',
      ).filter((employee) => employee.fEmpManagerId === myId);

      return sortSearchItems(accessibleEditableEnded);
    }

    return [];
  }, [
    canViewEndedEmployees,
    canViewEndedEmployeesTab,
    editableEndedEmployeesObject,
    accessibleEndedEmployeesObject,
    myId,
  ]);

  const getSearchResults = useCallback(
    (searchQuery: string) => {
      const searchItems = canSearchExtendedEmployeeSet
        ? getSearchableItems(
            allEmployees,
            getNewEmployeeList(),
            getEditableEndedEmpList(),
            companyData,
          )
        : allEmployees;

      return findMatchingItems(searchQuery, searchItems);
    },
    [
      allEmployees,
      companyData,
      canSearchExtendedEmployeeSet,
      getEditableEndedEmpList,
      getNewEmployeeList,
    ],
  );

  const getGroupedOrgUnitEmployees = (): GroupedOrgUnitEmps[] => {
    return sortBy(orgUnits, ['fOrgUnitName']).map((orgUnit) => ({
      name: orgUnit.fOrgUnitName,
      number: orgUnit.fOrgUnitNumber,
      employees: allEmployees.filter(
        (emp) => emp.fEmpUnit === orgUnit.fOrgUnitId,
      ),
    }));
  };

  return {
    allEmployees,
    getSearchResults,
    getTeamMemberList,
    getMatrixTeamMemberList,
    getNewEmployeeList,
    getEditableEndedEmpList,
    getGroupedOrgUnitEmployees,
    showEditableEndedEmployeeTab,
    hasMatrixTeam: !!rawEmpRelationData.hasMatrixTeam,
  };
};
